import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Typography, Button, Card } from 'antd';
import { QuestionCircleOutlined, CloseOutlined, CheckCircleFilled, LeftOutlined, RightOutlined } from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';

const { Text } = Typography;

const TOUR_STORAGE_KEY = 'gfs-tour-completed';

// All tour steps with their content and targets
const TOUR_STEPS = [
  // Dashboard steps (0-5)
  {
    title: 'Welcome to GFS Tracking!',
    description: 'Track GFS expenses across all Pita Way locations. Monitor costs, compare locations, and reduce spending.',
    target: null,
    page: 'any',
  },
  {
    title: 'Navigate Between Pages',
    description: 'Use the sidebar to switch pages: Dashboard for overview, Expenses for detailed item-level data.',
    target: '[data-tour="nav-dashboard"]',
    page: 'dashboard',
  },
  {
    title: 'Weekly vs Quarterly',
    description: 'Weekly shows prior week + 8-week rolling totals. Quarterly shows full quarter calculations.',
    target: '[data-tour="view-toggle"]',
    page: 'dashboard',
  },
  {
    title: 'Select Period',
    description: 'Pick the week you want to view. You can also see the baseline average date range here.',
    target: '[data-tour="date-selector"]',
    page: 'dashboard',
  },
  {
    title: 'Dashboard Table',
    description: 'GFS % = GFS Spending ÷ Sales. Phone screens summarize the data available on tablet/desktop - for full insight, use a tablet or laptop.',
    target: '[data-tour="tier-table"]',
    page: 'dashboard',
    position: 'top',
  },
  {
    title: 'Sales Tiers',
    description: 'Locations grouped by weekly sales: High (>$35K), Upper Mid ($25-35K), Lower Mid ($20-25K), Base (<$20K).',
    target: '[data-tour="tier-table"]',
    page: 'dashboard',
    position: 'top', // Panel shows at top when targeting table
  },
  // Expenses steps (6-10)
  {
    title: 'Expenses Page',
    description: 'See exactly what each location orders from GFS. Click "View" to expand item details.',
    target: '[data-tour="expenses-table"]',
    page: 'expenses',
    position: 'top',
  },
  {
    title: 'View Mode',
    description: 'Weekly shows 4-wk and 8-wk average columns. Quarterly shows full quarter totals only.',
    target: '[data-tour="expenses-filters"]',
    page: 'expenses',
  },
  {
    title: 'Select Period',
    description: 'Choose the reporting period to analyze. Weekly shows recent week data; Quarterly shows full quarter.',
    target: '[data-tour="expenses-filters"]',
    page: 'expenses',
  },
  {
    title: 'Critical vs All Items',
    description: 'When you expand a location: Critical Items shows grouped categories (e.g., "Chicken"), All Items shows every individual GFS product.',
    target: '[data-tour="expenses-table"]',
    page: 'expenses',
    position: 'top',
  },
  {
    title: 'Average Columns',
    description: '4-Wk Avg shows recent trend. 8-Wk Avg shows stable baseline. Higher than average? May need review!',
    target: '[data-tour="expenses-table"]',
    page: 'expenses',
    position: 'top',
  },
  // Compare steps (11) - this triggers interactive mode
  {
    title: 'Compare Locations',
    description: 'Click the Compare button now! The guide will walk you through selecting and comparing two locations.',
    target: '[data-tour="compare-section"]',
    page: 'expenses',
    isInteractive: true,
  },
  // Final step (12)
  {
    title: "You're All Set!",
    description: 'Check weekly for unusual orders. Use quarterly for trends. Click the ? button anytime to see this tour again.',
    target: null,
    page: 'any',
    isFinal: true,
  },
];

// Interactive guide steps for the Compare flow
const COMPARE_GUIDE_STEPS = [
  { key: 'select-a', title: 'Step 1', instruction: 'Select Location A (first dropdown)' },
  { key: 'select-b', title: 'Step 2', instruction: 'Select Location B (second dropdown)' },
  { key: 'click-compare', title: 'Step 3', instruction: 'Click the Compare button' },
  { key: 'explore', title: 'Step 4', instruction: 'Try "Align Items" to match rows side-by-side' },
  { key: 'done', title: 'Complete!', instruction: 'You can also download a PDF report' },
];

interface AppTourProps {
  isMobile: boolean;
}

export const AppTour: React.FC<AppTourProps> = ({ isMobile }) => {
  const [open, setOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [compareGuideActive, setCompareGuideActive] = useState(false);
  const [compareGuideStep, setCompareGuideStep] = useState(0);
  const [highlightedElement, setHighlightedElement] = useState<HTMLElement | null>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const totalSteps = TOUR_STEPS.length;
  const currentStepData = TOUR_STEPS[currentStep];

  // Check if user has seen the tour
  useEffect(() => {
    const hasSeenTour = localStorage.getItem(TOUR_STORAGE_KEY);
    if (!hasSeenTour) {
      const timer = setTimeout(() => setOpen(true), 500);
      return () => clearTimeout(timer);
    }
  }, []);

  // Highlight target element when step changes
  useEffect(() => {
    if (!open || !currentStepData?.target) {
      // Remove highlight
      if (highlightedElement) {
        highlightedElement.style.outline = '';
        highlightedElement.style.outlineOffset = '';
        setHighlightedElement(null);
      }
      return;
    }

    const targetSelector = currentStepData.target;
    const element = document.querySelector(targetSelector) as HTMLElement;

    if (element) {
      // Remove previous highlight
      if (highlightedElement && highlightedElement !== element) {
        highlightedElement.style.outline = '';
        highlightedElement.style.outlineOffset = '';
      }

      // Add highlight to current element (outline only - no z-index changes to avoid stacking issues)
      element.style.outline = '3px solid #7a1f2a';
      element.style.outlineOffset = '4px';
      setHighlightedElement(element);

      // Scroll element into view
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    return () => {
      if (element) {
        element.style.outline = '';
        element.style.outlineOffset = '';
      }
    };
  }, [open, currentStep, currentStepData, highlightedElement]);

  // Navigate to correct page when step changes
  useEffect(() => {
    if (!open) return;

    const stepData = TOUR_STEPS[currentStep];
    if (stepData.page === 'dashboard' && location.pathname !== '/dashboard') {
      navigate('/dashboard');
    } else if (stepData.page === 'expenses' && location.pathname !== '/expenses') {
      navigate('/expenses');
    }
  }, [open, currentStep, navigate, location.pathname]);

  // Watch for Compare modal and comparison view - make tour interactive
  useEffect(() => {
    if (!open && !compareGuideActive) return;

    const checkForModal = () => {
      // Look for Ant Design modal with "Compare Locations" title
      const modal = document.querySelector('.ant-modal-wrap:not([style*="display: none"])');
      const modalTitle = modal?.querySelector('.ant-modal-title');
      const isCompareModal = modalTitle?.textContent?.includes('Compare Locations');

      // Check for comparison view (after user clicks Compare in modal)
      const exitButton = Array.from(document.querySelectorAll('button')).find(
        btn => btn.textContent?.includes('Exit Comparison')
      );
      const isInComparisonView = !!exitButton;

      // Check for Align Items button
      const alignButton = Array.from(document.querySelectorAll('button')).find(
        btn => btn.textContent?.includes('Align') || btn.textContent?.includes('Items Aligned')
      );

      if (isCompareModal && open && currentStepData?.isInteractive) {
        // User opened the Compare modal - switch to interactive guide
        setOpen(false);
        setCompareGuideActive(true);
        setCompareGuideStep(0);
      } else if (compareGuideActive && isCompareModal) {
        // Track progress in the modal
        const selects = modal?.querySelectorAll('.ant-select-selection-item');
        const hasLocationA = selects && selects.length >= 1;
        const hasLocationB = selects && selects.length >= 2;

        if (hasLocationB && compareGuideStep < 2) {
          setCompareGuideStep(2); // Ready to click Compare
        } else if (hasLocationA && compareGuideStep < 1) {
          setCompareGuideStep(1); // Need to select Location B
        }
      } else if (isInComparisonView && compareGuideActive) {
        // User is now in comparison view
        if (compareGuideStep < 3) {
          setCompareGuideStep(3); // Show them Align Items
        }
        // Check if they clicked Align Items
        if (alignButton?.textContent?.includes('Aligned') && compareGuideStep < 4) {
          setCompareGuideStep(4); // Complete!
        }
      } else if (!isCompareModal && !isInComparisonView && compareGuideActive) {
        // User closed everything - end the guide
        setCompareGuideActive(false);
        setCompareGuideStep(0);
        setCurrentStep(totalSteps - 1); // Jump to final step
        setTimeout(() => setOpen(true), 300);
      }
    };

    const interval = setInterval(checkForModal, 200);
    return () => clearInterval(interval);
  }, [open, compareGuideActive, compareGuideStep, currentStepData, totalSteps]);

  const handleClose = useCallback(() => {
    setOpen(false);
    setCurrentStep(0);
    setCompareGuideActive(false);
    setCompareGuideStep(0);
    localStorage.setItem(TOUR_STORAGE_KEY, 'true');
  }, []);

  const handleSkipTour = useCallback(() => {
    setOpen(false);
    setCompareGuideActive(false);
    setCompareGuideStep(0);
    localStorage.setItem(TOUR_STORAGE_KEY, 'true');
  }, []);

  const handleOpen = useCallback(() => {
    setCurrentStep(0);
    setCompareGuideActive(false);
    setCompareGuideStep(0);
    // Start on dashboard
    if (location.pathname !== '/dashboard') {
      navigate('/dashboard');
    }
    setTimeout(() => setOpen(true), 100);
  }, [navigate, location.pathname]);

  const handleNext = useCallback(() => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleClose();
    }
  }, [currentStep, totalSteps, handleClose]);

  const handleBack = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  }, [currentStep]);

  // Get current guide step info for compare flow
  const currentGuideStep = COMPARE_GUIDE_STEPS[compareGuideStep];

  // Determine if panel should be at top or bottom
  const showAtTop = currentStepData?.position === 'top';

  // Shared panel styles - position changes based on step
  // On mobile, always show at top for table steps to avoid being covered
  const panelStyle: React.CSSProperties = {
    position: 'fixed',
    ...(showAtTop
      ? { top: isMobile ? 72 : 88, bottom: 'auto' }  // Below header
      : { bottom: isMobile ? 16 : 24, top: 'auto' }
    ),
    left: isMobile ? 16 : 'auto',
    right: isMobile ? 16 : 24,
    width: isMobile ? 'calc(100% - 32px)' : 340,
    maxWidth: isMobile ? 'none' : 340,
    zIndex: 9999, // Very high z-index to ensure it's always on top
    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.3)',
    borderRadius: 12,
    border: '2px solid #7a1f2a',
    backgroundColor: 'white', // Ensure solid background
  };

  const headerStyles = {
    header: {
      background: '#7a1f2a',
      borderRadius: '10px 10px 0 0',
      padding: '10px 14px',
      minHeight: 'auto',
    },
    body: {
      padding: 16,
    },
  };

  return (
    <>
      {/* Help button in header */}
      <div
        ref={buttonRef}
        onClick={handleOpen}
        style={{
          color: 'white',
          fontSize: 18,
          padding: 8,
          borderRadius: 8,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'background-color 0.2s',
          marginRight: isMobile ? 8 : 16,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.2)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
        }}
        title="Help Tour"
      >
        <QuestionCircleOutlined />
      </div>

      {/* Main Tour Panel */}
      {open && !compareGuideActive && (
        <Card
          size="small"
          style={panelStyle}
          styles={headerStyles}
          title={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text style={{ color: 'white', fontWeight: 600, fontSize: 14 }}>
                GFS Tracking Guide
              </Text>
              <Button
                type="text"
                size="small"
                icon={<CloseOutlined style={{ color: 'white', fontSize: 12 }} />}
                onClick={handleSkipTour}
                style={{ color: 'white', padding: 4, height: 'auto' }}
              />
            </div>
          }
        >
          {/* Progress indicator */}
          <div style={{ display: 'flex', gap: 3, marginBottom: 14 }}>
            {TOUR_STEPS.map((_, idx) => (
              <div
                key={idx}
                style={{
                  flex: 1,
                  height: 4,
                  borderRadius: 2,
                  backgroundColor: idx <= currentStep ? '#7a1f2a' : '#e8e8e8',
                  transition: 'background-color 0.3s',
                  cursor: 'pointer',
                }}
                onClick={() => setCurrentStep(idx)}
              />
            ))}
          </div>

          {/* Current step content */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 16 }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                backgroundColor: currentStepData?.isFinal ? '#52c41a' : '#7a1f2a',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              {currentStepData?.isFinal ? (
                <CheckCircleFilled style={{ color: 'white', fontSize: 18 }} />
              ) : (
                <Text style={{ color: 'white', fontWeight: 600, fontSize: 13 }}>
                  {currentStep + 1}
                </Text>
              )}
            </div>
            <div style={{ flex: 1 }}>
              <Text strong style={{ fontSize: 14, display: 'block', marginBottom: 4, color: '#1a1a1a' }}>
                {currentStepData?.title}
              </Text>
              <Text style={{ fontSize: 13, color: '#555', lineHeight: 1.5 }}>
                {currentStepData?.description}
              </Text>
            </div>
          </div>

          {/* Navigation buttons */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Button
              size="small"
              icon={<LeftOutlined />}
              onClick={handleBack}
              disabled={currentStep === 0}
              style={{ visibility: currentStep === 0 ? 'hidden' : 'visible' }}
            >
              Back
            </Button>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {currentStep + 1} of {totalSteps}
            </Text>
            {currentStepData?.isFinal ? (
              <Button size="small" type="primary" onClick={handleClose} style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}>
                Finish
              </Button>
            ) : currentStepData?.isInteractive ? (
              <Button size="small" type="primary" style={{ backgroundColor: '#7a1f2a', borderColor: '#7a1f2a' }}>
                Click Compare →
              </Button>
            ) : (
              <Button
                size="small"
                type="primary"
                onClick={handleNext}
                style={{ backgroundColor: '#7a1f2a', borderColor: '#7a1f2a' }}
              >
                Next <RightOutlined />
              </Button>
            )}
          </div>
        </Card>
      )}

      {/* Interactive Compare Guide Panel - always at bottom */}
      {compareGuideActive && (
        <Card
          size="small"
          style={{
            ...panelStyle,
            top: 'auto',
            bottom: isMobile ? 16 : 24,
          }}
          styles={headerStyles}
          title={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text style={{ color: 'white', fontWeight: 600, fontSize: 14 }}>
                Compare Locations Guide
              </Text>
              <Button
                type="text"
                size="small"
                icon={<CloseOutlined style={{ color: 'white', fontSize: 12 }} />}
                onClick={handleSkipTour}
                style={{ color: 'white', padding: 4, height: 'auto' }}
              />
            </div>
          }
        >
          {/* Progress indicator */}
          <div style={{ display: 'flex', gap: 4, marginBottom: 14 }}>
            {COMPARE_GUIDE_STEPS.map((step, idx) => (
              <div
                key={step.key}
                style={{
                  flex: 1,
                  height: 4,
                  borderRadius: 2,
                  backgroundColor: idx <= compareGuideStep ? '#7a1f2a' : '#e8e8e8',
                  transition: 'background-color 0.3s',
                }}
              />
            ))}
          </div>

          {/* Current step */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                backgroundColor: compareGuideStep === 4 ? '#52c41a' : '#7a1f2a',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              {compareGuideStep === 4 ? (
                <CheckCircleFilled style={{ color: 'white', fontSize: 18 }} />
              ) : (
                <Text style={{ color: 'white', fontWeight: 600, fontSize: 13 }}>
                  {compareGuideStep + 1}
                </Text>
              )}
            </div>
            <div>
              <Text strong style={{ fontSize: 14, display: 'block', marginBottom: 4, color: '#1a1a1a' }}>
                {currentGuideStep.title}
              </Text>
              <Text style={{ fontSize: 13, color: '#555' }}>
                {currentGuideStep.instruction}
              </Text>
            </div>
          </div>

          {/* Skip/Finish button */}
          <div style={{ marginTop: 14, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              size="small"
              type={compareGuideStep === 4 ? 'primary' : 'default'}
              onClick={handleSkipTour}
              style={compareGuideStep === 4 ? { backgroundColor: '#52c41a', borderColor: '#52c41a' } : {}}
            >
              {compareGuideStep === 4 ? 'Finish Tour' : 'Skip Tutorial'}
            </Button>
          </div>
        </Card>
      )}
    </>
  );
};

export default AppTour;
